import { useMemo, useState } from 'react'
import { useLocationTree } from '../../api/hooks'
import type { LocationNode } from '../../api/types'
import { locationPaths } from '../../lib/locations'

function flatten(nodes: LocationNode[], depth = 0): { uid: string; name: string; depth: number }[] {
  return nodes.flatMap((n) => [{ uid: n.unique_id, name: n.name, depth }, ...flatten(n.children, depth + 1)])
}

/**
 * Pick any number of locations at any tree level: the selected set as
 * removable path rows, a path filter, and the `— `-indented tree as
 * checkboxes. Controlled — `value` is the full selection, `onChange` gets
 * the new full selection (added uids append in click order).
 *
 * Used by the zone editor and the camera event-bookmark coverage card.
 */
export function LocationMultiPicker({
  value,
  onChange,
  label,
  disabled = false,
  exclude = [],
}: {
  value: string[]
  onChange: (next: string[]) => void
  /** Accessible name of the tree list (e.g. "Zone locations"). */
  label: string
  disabled?: boolean
  /** Locations to hide from the pickable tree (already covered another
   *  way — e.g. the camera's own placement). */
  exclude?: string[]
}) {
  const tree = useLocationTree()
  const [filter, setFilter] = useState('')

  const paths = useMemo(() => locationPaths(tree.data ?? []), [tree.data])
  const flat = useMemo(() => {
    const all = flatten(tree.data ?? [])
    return exclude.length === 0 ? all : all.filter((l) => !exclude.includes(l.uid))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- exclude compared by value below
  }, [tree.data, exclude.join(',')])
  const needle = filter.trim().toLowerCase()
  const visible = needle ? flat.filter((l) => (paths.get(l.uid) ?? l.name).toLowerCase().includes(needle)) : flat

  const toggle = (uid: string) => onChange(value.includes(uid) ? value.filter((u) => u !== uid) : [...value, uid])

  return (
    <>
      {value.length > 0 && (
        <ul aria-label="Selected locations" className="flex flex-col gap-1">
          {value.map((uid) => (
            <li
              key={uid}
              className="flex items-center gap-2 rounded-sm border border-ostrel-line bg-ostrel-sand px-2 py-1 text-xs text-ostrel-ink"
            >
              <span className="min-w-0 flex-1 truncate">{paths.get(uid) ?? uid}</span>
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove ${paths.get(uid) ?? uid}`}
                  onClick={() => toggle(uid)}
                  className="px-1 text-ostrel-ink-muted hover:text-ostrel-danger focus:outline-none focus:ring-2 focus:ring-ostrel-gold"
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {!disabled && (
        <>
          <input
            type="search"
            aria-label="Filter locations"
            placeholder="Filter locations…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-1.5 text-xs text-ostrel-ink placeholder:text-ostrel-ink-muted focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
          />
          <div
            aria-label={label}
            className="flex max-h-56 flex-col gap-0.5 overflow-y-auto rounded-sm border border-ostrel-line bg-ostrel-card px-2 py-1.5"
          >
            {visible.length === 0 ? (
              <p className="px-1.5 py-1 text-[11px] text-ostrel-ink-muted">No locations.</p>
            ) : (
              visible.map((l) => (
                <label
                  key={l.uid}
                  className="flex items-center gap-2 rounded-sm px-1.5 py-1 text-xs text-ostrel-ink hover:bg-ostrel-sand"
                >
                  <input
                    type="checkbox"
                    checked={value.includes(l.uid)}
                    onChange={() => toggle(l.uid)}
                    className="accent-ostrel-gold"
                  />
                  <span className="min-w-0 truncate">
                    {needle ? (paths.get(l.uid) ?? l.name) : `${'— '.repeat(l.depth)}${l.name}`}
                  </span>
                </label>
              ))
            )}
          </div>
        </>
      )}
    </>
  )
}
